import { 
  collection, 
  addDoc,
  updateDoc,
  getDocs, 
  deleteDoc,
  doc,
  query,
  orderBy
} from 'firebase/firestore';
import { Collections, getCollection } from './collections';
import { handleFirebaseError } from './errors';
import type { JobSeekerProfile, JobSeekerProfileData } from '../../types/profiles';

function validateProfileData(data: JobSeekerProfileData): boolean {
  return (
    Array.isArray(data.jobTypes) && data.jobTypes.length > 0 &&
    Array.isArray(data.locations) && data.locations.length > 0 &&
    Array.isArray(data.skills) && data.skills.length > 0 &&
    typeof data.experience === 'string' && data.experience.length > 0 &&
    typeof data.availability === 'string' && data.availability.length > 0 &&
    typeof data.desiredPay === 'string' &&
    Array.isArray(data.certifications) &&
    typeof data.about === 'string'
  );
}

export async function addJobSeekerProfile(
  userId: string,
  profileData: JobSeekerProfileData
) {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    if (!validateProfileData(profileData)) {
      throw new Error('Invalid profile data');
    }

    // Clean empty strings from arrays
    const cleanedData = {
      ...profileData,
      locations: profileData.locations.filter(Boolean),
      skills: profileData.skills.filter(Boolean),
      certifications: profileData.certifications.filter(Boolean)
    };

    const profilesCollection = getCollection(Collections.JOB_SEEKERS);
    const docRef = await addDoc(profilesCollection, {
      ...cleanedData,
      userId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    
    console.log('Successfully created job seeker profile:', docRef.id);
    return { id: docRef.id, error: null };
  } catch (error) {
    console.error('Error in addJobSeekerProfile:', error);
    return { id: null, error: handleFirebaseError(error) };
  }
}

export async function updateJobSeekerProfile(
  profileId: string,
  profileData: Partial<JobSeekerProfileData>
) {
  try {
    // Clean empty strings from arrays
    const cleanedData = {
      ...profileData,
      locations: profileData.locations?.filter(Boolean),
      skills: profileData.skills?.filter(Boolean),
      certifications: profileData.certifications?.filter(Boolean)
    };

    const profileRef = doc(getCollection(Collections.JOB_SEEKERS), profileId);
    await updateDoc(profileRef, {
      ...cleanedData,
      updatedAt: new Date().toISOString()
    });
    return { error: null };
  } catch (error) {
    return { error: handleFirebaseError(error) };
  }
}

export async function getJobSeekerProfiles() {
  try {
    const profilesCollection = getCollection(Collections.JOB_SEEKERS);
    const q = query(profilesCollection, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    
    const profiles = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as JobSeekerProfile[];
    
    return { profiles, error: null };
  } catch (error) {
    return { profiles: [], error: handleFirebaseError(error) };
  }
}

export async function deleteJobSeekerProfile(profileId: string) {
  try {
    const profileRef = doc(getCollection(Collections.JOB_SEEKERS), profileId);
    await deleteDoc(profileRef);
    return { error: null };
  } catch (error) {
    return { error: handleFirebaseError(error) };
  }
}